import { useState, useRef } from 'react'
import { NODE_DEFAULTS } from '../../../../../utils/nodeDefaults'
import { adjustDropdownPosition } from '../../../../../utils/dropdownPosition'

interface UseCollapsedPreviewParams {
  isCollapsed: boolean
}

export const useCollapsedPreview = ({ isCollapsed }: UseCollapsedPreviewParams) => {
  const [isHovered, setIsHovered] = useState(false)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Clear any pending hover timeout
  const clearHoverTimeout = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
  }

  // Handle mouse enter/leave for collapsed node preview
  const handleMouseEnter = (e: React.MouseEvent) => {
    // Don't show preview if mouse button is pressed (user is dragging)
    // e.buttons === 0 means no buttons pressed
    if (isCollapsed && e.buttons === 0) {
      // Estimate preview dimensions (width from NODE_DEFAULTS, height approximate)
      const previewDimensions = { width: NODE_DEFAULTS.width, height: 200 }

      // Position preview to the right of the cursor with a small gap
      const idealPosition = {
        x: e.clientX + 15,
        y: e.clientY - previewDimensions.height / 2,
      }

      // Adjust position to keep preview within viewport bounds
      const adjustedPosition = adjustDropdownPosition(idealPosition, previewDimensions)

      setMousePosition(adjustedPosition)

      // Delay showing preview by 500ms
      clearHoverTimeout()
      hoverTimeoutRef.current = setTimeout(() => {
        setIsHovered(true)
      }, 500)
    }
  }

  const handleMouseLeave = () => {
    clearHoverTimeout()
    setIsHovered(false)
  }

  // Hide preview when starting to drag
  const handleMouseDown = () => {
    clearHoverTimeout()
  }

  // Hide preview if mouse button becomes pressed while hovering
  const handleMouseMove = (e: React.MouseEvent) => {
    if (e.buttons !== 0) {
      clearHoverTimeout()
      setIsHovered(false)
    }
  }

  return {
    isHovered,
    mousePosition,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    handleMouseMove,
  }
}
